"use client";

import { useEffect, useId, useRef, useState } from "react";
import { CHECK_AVAILABILITY_LABEL } from "@shared/products/listing-copy";
import { parsePositiveQuantity } from "@shared/validation/positive-number";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AddressInput } from "@/components/inputs/address-input";
import type { ProductDetail } from "./types";
import {
  QuoteDeliveryDateField,
  QuoteQuantityField,
} from "@/components/quotes/listing-request/request-fields";
import { ListingQuoteFulfillmentField } from "@/components/quotes/listing-request/fulfillment-field";
import { ListingGuestContactFields } from "./listing-guest-contact-fields";
import type { QuoteStartField } from "./listing-quote-tracking";
import {
  focusQuoteRequirement,
  pickTouchedErrors,
  type QuoteRequirementErrors,
  type QuoteRequirementField,
  type QuoteValidity,
  isStreetUnpicked,
} from "./listing-quote-requirements";
import type {
  QuoteContactMode,
  QuoteFormState,
  UpdateQuoteFormState,
} from "./listing-quote-request-state";

interface QuoteSubmission {
  submit: () => Promise<void>;
  isLoading: boolean;
  error: string | null | undefined;
  clearError: () => void;
}

export interface QuoteRequestFieldsProps {
  data: ProductDetail;
  contactMode: QuoteContactMode;
  pickupAvailable: boolean;
  state: QuoteFormState;
  submission: QuoteSubmission;
  updateState: UpdateQuoteFormState;
  markStarted: (field: QuoteStartField) => void;
  validity: QuoteValidity;
  contactEmail: string;
  contactPhone: string;
}

function ignoreUpdates() {}

function shownErrors(props: QuoteRequestFieldsProps): QuoteRequirementErrors {
  return pickTouchedErrors(props.validity.errors, props.state.touched);
}

function leaveField(props: QuoteRequestFieldsProps, field: QuoteRequirementField) {
  props.updateState({ touched: { ...props.state.touched, [field]: true } });
}

function useFocusFirstBlockedField() {
  const formRef = useRef<HTMLFormElement>(null);
  const [request, setRequest] = useState<{
    field: QuoteRequirementField;
  } | null>(null);
  useEffect(() => {
    if (request) focusQuoteRequirement(formRef.current, request.field);
  }, [request]);
  return {
    formRef,
    focusField: (field: QuoteRequirementField) => setRequest({ field }),
  };
}

export function QuoteRequestFields(submittedProps: QuoteRequestFieldsProps) {
  const props = submittedProps.submission.isLoading
    ? { ...submittedProps, updateState: ignoreUpdates }
    : submittedProps;
  const { formRef, focusField } = useFocusFirstBlockedField();
  const blockedByField = props.validity.blockedByField;
  return (
    <form
      ref={formRef}
      className="space-y-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (blockedByField && !props.submission.isLoading) {
          focusField(blockedByField);
        }
        void props.submission.submit();
      }}
    >
      {props.contactMode === "account" ? <AccountAutofillInputs {...props} /> : null}
      <fieldset disabled={props.submission.isLoading} className="min-w-0 space-y-4">
        <QuotePrimaryFields {...props} />
        <QuoteSecondaryFields {...props} />
        {props.contactMode === "account" ? null : (
          <ListingGuestContactFields
            contact={props.state.contact}
            updateState={props.updateState}
            onEdit={props.submission.clearError}
            onLeave={(field) => leaveField(props, field)}
            errors={shownErrors(props)}
            includeEmail={props.contactMode === "guest"}
          />
        )}
      </fieldset>
      <QuoteRequestActions {...props} />
    </form>
  );
}

function AccountAutofillInputs(props: QuoteRequestFieldsProps) {
  return (
    <div aria-hidden="true" className="sr-only">
      <input
        type="email"
        name="email"
        autoComplete="email"
        value={props.contactEmail}
        readOnly
        tabIndex={-1}
      />
      <input
        type="tel"
        name="phone"
        autoComplete="tel"
        value={props.contactPhone}
        readOnly
        tabIndex={-1}
      />
    </div>
  );
}

function QuotePrimaryFields(props: QuoteRequestFieldsProps) {
  const { state, submission, updateState } = props;
  const errors = shownErrors(props);
  return (
    <>
      <div data-quote-requirement="fulfillmentType">
        <ListingQuoteFulfillmentField
          value={state.fulfillmentType}
          onChange={(fulfillmentType) => {
            submission.clearError();
            updateState({ fulfillmentType });
          }}
          pickupLocality={props.data.pickupAddress}
          error={errors.fulfillmentType}
        />
      </div>
      <div data-quote-requirement="quantity">
        <QuoteQuantityField
          displayValue={state.quantityDisplay}
          error={errors.quantity}
          onChange={(quantityDisplay) => {
            submission.clearError();
            const parsedQuantity = parsePositiveQuantity(quantityDisplay);
            updateState({
              quantityDisplay,
              quantity: parsedQuantity ?? 0,
            });
          }}
          onBlur={() => leaveField(props, "quantity")}
        />
      </div>
      {state.fulfillmentType === "delivery" ? (
        <QuoteDeliveryAddressField {...props} />
      ) : null}
    </>
  );
}

function QuoteDeliveryAddressField(props: QuoteRequestFieldsProps) {
  const { state, submission, updateState } = props;
  const error = shownErrors(props).deliveryAddress;
  const streetError = error && isStreetUnpicked(state.deliveryAddress) ? error : undefined;
  const fieldsetError = streetError ? undefined : error;
  const errorId = useId();
  return (
    <fieldset
      className="mt-4 space-y-2"
      data-quote-requirement="deliveryAddress"
      aria-invalid={fieldsetError ? true : undefined}
      aria-describedby={fieldsetError ? errorId : undefined}
      onInput={() => props.markStarted("deliveryAddress")}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          leaveField(props, "deliveryAddress");
        }
      }}
    >
      <legend className="mb-2 text-base font-semibold text-foreground">
        Delivery address
      </legend>
      <AddressInput
        value={state.deliveryAddress}
        onChange={(deliveryAddress) => {
          submission.clearError();
          updateState({ deliveryAddress });
        }}
        errors={streetError ? { line1: streetError } : undefined}
      />
      {fieldsetError ? (
        <p id={errorId} className="text-xs text-destructive-strong">
          {fieldsetError}
        </p>
      ) : null}
    </fieldset>
  );
}

function QuoteSecondaryFields(props: QuoteRequestFieldsProps) {
  const { data, state, submission, updateState } = props;
  return (
    <>
      <div data-quote-requirement="preferredDate">
        <QuoteDeliveryDateField
          fulfillmentType={state.fulfillmentType}
          value={state.preferredDate}
          error={shownErrors(props).preferredDate}
          onChange={(preferredDate) => {
            submission.clearError();
            updateState({ preferredDate });
          }}
          leadTimeDays={data.leadTimeDays ?? 0}
          open={state.datePickerOpen}
          onOpenChange={(datePickerOpen) => {
            updateState({ datePickerOpen });
            if (!datePickerOpen) leaveField(props, "preferredDate");
          }}
        />
      </div>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">Notes</span>
        <Textarea
          value={state.notes}
          onChange={(event) => {
            submission.clearError();
            updateState({ notes: event.target.value });
          }}
          placeholder="Delivery constraints, dock details, timing preferences…"
          rows={3}
        />
      </label>
    </>
  );
}

function QuoteRequestActions(props: QuoteRequestFieldsProps) {
  const { submission } = props;
  return (
    <>
      <ActionErrorAlert from={submission} />
      <Button
        variant="primary"
        type="submit"
        className="w-full"
        disabled={submission.isLoading}
      >
        {submission.isLoading ? "Sending request…" : CHECK_AVAILABILITY_LABEL}
      </Button>
    </>
  );
}
